/**
 * <img> replacement for backend-served media that sits behind JWT auth.
 *
 * The drama thumbnail / avatar endpoints require an `Authorization: Bearer`
 * header (middleware/auth.py). A plain `<img src>` is a browser GET that does
 * NOT carry that header (and also bypasses the axios baseURL, so it would miss
 * the `/api/v1` prefix and the Vite dev proxy). Both problems vanish if we
 * fetch the bytes through the shared axios client — it injects the token, the
 * `/api/v1` baseURL, and the 401-refresh interceptor — then render the result
 * as an object URL.
 *
 * Pass `path` as the axios-relative URL (exactly what `dramaApi.*Url(...)`
 * returns, e.g. "/drama-projects/{id}/episodes/1/thumbnail").
 */
import { useEffect, useRef, useState } from 'react'

import { api } from '@/lib/api'

interface AuthedImageProps {
  path: string
  alt?: string
  className?: string
  style?: React.CSSProperties
  /** Mirrors native <img onError>: fired when the fetch fails (404/401/etc.)
   *  so callers can fall back to a placeholder. */
  onError?: () => void
}

export function AuthedImage({ path, alt, className, style, onError }: AuthedImageProps) {
  const [src, setSrc] = useState<string | null>(null)
  // Keep onError out of the effect deps (callers pass fresh closures each
  // render) so we don't refetch the image on every parent re-render.
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    let active = true
    let objectUrl: string | null = null
    setSrc(null)
    api.client
      .get(path, { responseType: 'blob' })
      .then((res) => {
        if (!active) return
        objectUrl = URL.createObjectURL(res.data as Blob)
        setSrc(objectUrl)
      })
      .catch(() => {
        if (active) onErrorRef.current?.()
      })
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path])

  // Render nothing until the bytes arrive; the caller's container shows its
  // own background/placeholder in the meantime (and permanently on error).
  if (!src) return null
  return <img src={src} alt={alt} className={className} style={style} />
}
